import React, { useCallback, useMemo, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import { IconName } from '@fortawesome/sharp-regular-svg-icons';
import { ConfirmationModal, useModal } from '@europrocurement/flexy-components';
import {
    DossierRoutingContext,
    DossierValidatorStatus,
    PROCESS_STATUS_FLAG_DONE,
    PROCESS_STATUS_FLAG_ONGOING,
    REFRESH_CURRENT_DOSSIER_CTX,
    RESET_DOS_DOCS_CTX,
    useCurrentDossierCtx,
    useCurrentDossierCtxDispatch,
    useDossierDocumentsCtxDispatch,
    useDossierProcesses,
    useDossierProcessesActionsAndRender,
    useDossierValidatorsCtx,
    VALIDATOR_DEMAT_FACILE,
    VALIDATOR_TYPE_GREFFE,
} from '@europrocurement/l2d-modules';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import { DossierOpenSearch, NestedDossierProcessesOpenSearch } from '@europrocurement/l2d-domain';
import DossierFormaliteProcessListRows from './DossierFormaliteProcessListRows';
import DossierFormaliteProcessListHeaderCells from './DossierFormaliteProcessListHeaderCells';
import UpdateValidatorForm from '../UpdateValidatorForm/UpdateValidatorForm';
import DossierProcessesBulkActionsBar from '../DossierProcessesBulkActionsBar/DossierProcessesBulkActionsBar';
import useDossierBulkActions from '../../hooks/useDossierBulkActions';

export type DossierFormaliteProcessProps = {
    libelle: string;
    documentsCount: number;
    complete: boolean;
    optionsCount: number;
    externalUrl?: string;
    onExternalUrlClick: (externalUrl: string) => void;
    id: number;
    prestationId: number[];
    greffes?: Array<{ id?: number; libelle?: string }>;
};

export type DossierFormaliteProcessesListProps = {
    formalities: DossierFormaliteProcessProps[];
    publishable: boolean;
};

export type SelectedFormality = {
    id: number;
};

const DossierFormaliteProcessesList: React.FunctionComponent<DossierFormaliteProcessesListProps> =
    function (props) {
        const { formalities, publishable } = props;
        const { modalActions } = useModal();
        const { dossier } = useCurrentDossierCtx();
        const dispatch = useCurrentDossierCtxDispatch();
        const dossierDocumentsdispatch = useDossierDocumentsCtxDispatch();
        const navigate = useNavigate();
        const { getDossierRoute } = React.useContext(DossierRoutingContext);
        const { palette } = useTheme();

        const refreshDossier = useCallback(
            (dossierId?: number) => {
                if (dispatch) {
                    dispatch({ type: REFRESH_CURRENT_DOSSIER_CTX });
                }
                if (dossierDocumentsdispatch) {
                    dossierDocumentsdispatch({ type: RESET_DOS_DOCS_CTX });
                }
                if (dossierId) {
                    const route = getDossierRoute(dossierId as number);
                    if (route) {
                        navigate(route);
                    }
                }
            },
            [dispatch, dossierDocumentsdispatch, getDossierRoute, navigate],
        );

        const { enqueueSnackbar } = useSnackbar();
        const {
            getCurrentProcessOfType,
            canChangeProcessStatus,
            toggleGreffeValidator,
            canToggleGreffeValidator,
            getNotPublishableReason,
        } = useDossierProcesses();
        const { actionUpdateValidatorStatus, handleUpdateStatusChange } =
            useDossierProcessesActionsAndRender();

        const process = useMemo(
            () => (dossier ? getCurrentProcessOfType(dossier, VALIDATOR_TYPE_GREFFE) : undefined),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [dossier],
        );

        const { getDossierValidatorById } = useDossierValidatorsCtx();
        const statuses = process
            ? getDossierValidatorById(process.validatorId)?.statuses || []
            : [];

        const [selectedFormalitiesIds, setSelectedFormalitiesIds] = useState<number[]>([]);

        const { canDissociate, canMove, handleDissociate, handleMove } = useDossierBulkActions({
            selectedIds: selectedFormalitiesIds,
            refreshDossier,
            idType: 'formality',
        });

        const onStatusChange = async (
            updatedProcess: NestedDossierProcessesOpenSearch,
            newStatus: DossierValidatorStatus,
        ) => {
            try {
                await handleUpdateStatusChange(updatedProcess, newStatus.code);
            } catch (e) {
                console.error(e);
                enqueueSnackbar(
                    'Une erreur est survenue lors de la mise à jour de votre démarche',
                    {
                        variant: 'error',
                    },
                );
            } finally {
                modalActions.close();
                refreshDossier();
            }
        };

        const handleOnOpenUpdateStatusModal = () => {
            if (!process || !canChangeProcessStatus(process)) {
                return;
            }

            modalActions.call(
                <UpdateValidatorForm
                    processing={false}
                    process={process}
                    onStatusChanged={(newStatus) => {
                        if (newStatus.statusFlag !== PROCESS_STATUS_FLAG_DONE) {
                            onStatusChange(process, newStatus);

                            return;
                        }

                        modalActions.call(
                            <ConfirmationModal
                                color={palette.warning}
                                messages={{
                                    question: 'Confirmer cette action ?',
                                    detail: {
                                        message: `Êtes-vous sûr(e) de vouloir passer au statut "${newStatus.label}" ? Cette action est définitive.`,
                                        severity: 'warning',
                                    },
                                    validation: 'Confirmer',
                                    annulation: 'Annuler',
                                }}
                                actionOnValidation={() => {
                                    onStatusChange(process, newStatus);
                                }}
                                actionOnCancellation={() => {
                                    modalActions.close();
                                }}
                            />,
                        );
                    }}
                    statuses={statuses.filter(
                        (status) =>
                            status.statusFlag !== PROCESS_STATUS_FLAG_ONGOING || publishable,
                    )}
                />,
            );
        };

        const handleToggleValidator = async () => {
            if (!process) {
                return;
            }

            try {
                await toggleGreffeValidator(dossier as DossierOpenSearch, process);
                refreshDossier();
            } catch (e) {
                enqueueSnackbar('Une erreur est survenue', { variant: 'error' });
                console.error(e);
            }
        };

        const allowChangeProcessStatus = process && canChangeProcessStatus(process);

        const getItemActions = (item: DossierFormaliteProcessProps) => {
            const itemActions = [];

            if (allowChangeProcessStatus) {
                itemActions.push(actionUpdateValidatorStatus(handleOnOpenUpdateStatusModal));
            }

            if (
                process &&
                dossier &&
                process.validatorTypeId === VALIDATOR_TYPE_GREFFE &&
                canToggleGreffeValidator(dossier)
            ) {
                itemActions.push({
                    label:
                        process.validatorId === VALIDATOR_DEMAT_FACILE
                            ? 'Passer en Guichet Unique'
                            : 'Passer en Demat Facile',
                    icon: 'rotate-reverse' as IconName,
                    onClick: handleToggleValidator,
                });
            }

            if (canMove) {
                itemActions.push({
                    label: 'Déplacer',
                    icon: 'file-import' as IconName,
                    onClick: () => handleMove([item.id]),
                });
            }

            if (canDissociate) {
                itemActions.push({
                    label: 'Dissocier',
                    icon: 'folders' as IconName,
                    onClick: () => handleDissociate([item.id]),
                });
            }

            return itemActions;
        };

        return (
            <Box
                sx={{
                    borderCollapse: 'separate',
                    borderSpacing: '0 4px',
                }}
            >
                <Box>
                    <DossierFormaliteProcessListHeaderCells showStatus={process !== undefined} />
                </Box>

                <DossierProcessesBulkActionsBar
                    selectedIds={selectedFormalitiesIds}
                    onClearSelection={() => setSelectedFormalitiesIds([])}
                    canDissociate={canDissociate}
                    onDissociate={handleDissociate}
                    canMove={canMove}
                    onMove={handleMove}
                />

                <Box>
                    <DossierFormaliteProcessListRows
                        formalities={formalities}
                        selectedFormalitiesIds={selectedFormalitiesIds}
                        setSelectedFormalitiesIds={setSelectedFormalitiesIds}
                        getItemActions={getItemActions}
                        process={
                            process
                                ? {
                                      validatorId: process.validatorId as number,
                                      validatorLabel: process.validatorLabel as string,
                                      statusLabel: process.statusLabel,
                                      statusFlag: process.statusFlag,
                                      onStatusClick: allowChangeProcessStatus
                                          ? handleOnOpenUpdateStatusModal
                                          : undefined,
                                  }
                                : undefined
                        }
                        warningReason={getNotPublishableReason(dossier as DossierOpenSearch)}
                    />
                </Box>
            </Box>
        );
    };

export default DossierFormaliteProcessesList;
